import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useRef, useEffect } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import { observer } from 'mobx-react-lite';
import AvatarEditor from 'react-avatar-editor';
import { userStore } from '../stores/UserStore';
import { getProfilePictureUrl } from '../utils/imageUtils';
const ProfilePictureUploadDialog = observer(({ show, onHide, userId, userName, currentProfilePicture }) => {
    const [selectedImage, setSelectedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [scale, setScale] = useState(1);
    const [errors, setErrors] = useState({});
    const editorRef = useRef(null);
    const fileInputRef = useRef(null);
    useEffect(() => {
        if (show) {
            // Set current profile picture as preview if exists
            if (currentProfilePicture) {
                const profilePicUrl = getProfilePictureUrl(currentProfilePicture);
                setImagePreview(profilePicUrl);
            }
            else {
                setImagePreview(null);
            }
            setSelectedImage(null);
            setScale(1);
            setErrors({});
        }
    }, [show, currentProfilePicture]);
    const handleImageSelect = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                setErrors(prev => ({ ...prev, profilePicture: 'Image size must be less than 5MB' }));
                return;
            }
            if (!file.type.startsWith('image/')) {
                setErrors(prev => ({ ...prev, profilePicture: 'Please select an image file' }));
                return;
            }
            setSelectedImage(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.profilePicture;
                return newErrors;
            });
        }
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        let profilePictureFile = undefined;
        if (selectedImage && editorRef.current) {
            // Get the canvas from avatar editor
            const canvas = editorRef.current.getImageScaledToCanvas();
            // Convert canvas to blob, then to File
            profilePictureFile = await new Promise((resolve, reject) => {
                canvas.toBlob((blob) => {
                    if (blob) {
                        const file = new File([blob], 'profile.jpg', { type: 'image/jpeg' });
                        resolve(file);
                    }
                    else {
                        reject(new Error('Failed to convert image'));
                    }
                }, 'image/jpeg', 0.9);
            });
        }
        else if (!selectedImage && !currentProfilePicture) {
            // No image selected and no existing image
            setErrors({ profilePicture: 'Please select an image to upload' });
            return;
        }
        try {
            await userStore.updateUser(userId, {}, profilePictureFile);
            onHide();
        }
        catch (error) {
            console.error('Error uploading profile picture:', error);
        }
    };
    const handleClose = () => {
        setSelectedImage(null);
        setImagePreview(null);
        setScale(1);
        setErrors({});
        onHide();
    };
    return (_jsxs(Modal, { show: show, onHide: handleClose, size: "lg", centered: true, children: [_jsx(Modal.Header, { closeButton: true, children: _jsx(Modal.Title, { children: "Upload Profile Picture" }) }), _jsxs(Form, { onSubmit: handleSubmit, children: [_jsxs(Modal.Body, { children: [userStore.error && (_jsx(Alert, { variant: "danger", className: "mb-3", children: userStore.error })), _jsx("div", { className: "text-center mb-4", children: _jsxs("p", { className: "text-muted", children: ["Upload profile picture for ", _jsx("strong", { children: userName })] }) }), _jsxs("div", { className: "d-flex flex-column align-items-center", children: [imagePreview && (_jsxs("div", { className: "mb-3 position-relative", children: [_jsx(AvatarEditor, { ref: editorRef, image: imagePreview, width: 256, height: 256, border: 10, borderRadius: 128, scale: scale, rotate: 0, style: { width: '100%', height: 'auto', maxWidth: '256px' } }), selectedImage && (_jsxs("div", { className: "mt-3", children: [_jsx(Form.Label, { className: "small", children: "Zoom" }), _jsx(Form.Range, { min: "1", max: "3", step: "0.1", value: scale, onChange: (e) => setScale(parseFloat(e.target.value)) })] }))] })), _jsxs("div", { className: "d-flex gap-2 flex-wrap justify-content-center", children: [_jsx(Form.Control, { ref: fileInputRef, type: "file", accept: "image/*", onChange: handleImageSelect, style: { display: 'none' }, id: "profile-picture-input" }), _jsxs(Form.Label, { htmlFor: "profile-picture-input", className: "btn btn-outline-primary btn-sm mb-0", style: { cursor: 'pointer', minHeight: 'var(--touch-target-min)' }, children: [_jsx("i", { className: "bi bi-upload me-1" }), imagePreview ? 'Change Image' : 'Select Image'] })] }), errors.profilePicture && (_jsx("div", { className: "text-danger small mt-2", children: errors.profilePicture })), _jsx(Form.Text, { className: "text-muted mt-2 text-center d-block", children: "Image will be resized to 256x256 pixels. Maximum file size: 5MB" })] })] }), _jsxs(Modal.Footer, { children: [_jsx(Button, { variant: "secondary", onClick: handleClose, className: "btn-touch w-mobile-100", children: "Cancel" }), _jsx(Button, { variant: "primary", type: "submit", disabled: userStore.loading || !selectedImage, className: "btn-touch w-mobile-100", children: userStore.loading ? 'Uploading...' : 'Upload Picture' })] })] })] }));
});
export default ProfilePictureUploadDialog;
